/* Section-rendered suggestions share Shopify's prices, locale and product URLs. */
(() => {
  if (customElements.get('quick-search')) return;
  const cache = new Map();
  class QuickSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type=search]');
      this.results = this.querySelector('[data-qs-results]');
      this.status = this.querySelector('[data-qs-status]');
      this.clear = this.querySelector('[data-qs-clear]');
      if (!this.input || !this.results) return;
      this.listeners?.abort(); this.listeners = new AbortController();
      const options = {signal: this.listeners.signal};
      this.input.addEventListener('input', event => { if (!event.isComposing) this.schedule(); }, options);
      this.input.addEventListener('compositionstart', () => this.dismiss(), options);
      this.input.addEventListener('compositionend', () => this.schedule(), options);
      this.input.addEventListener('focus', () => { if (!this.suppressFocus && this.results.hidden && this.input.value.trim() && this.lastQuery === this.input.value.trim()) this.schedule(); }, options);
      this.clear.addEventListener('click', () => { this.input.value = ''; this.schedule(); this.input.focus(); }, options);
      this.addEventListener('keydown', event => this.onKey(event), options);
      this.addEventListener('focusin', event => this.selectResult(event.target), options);
      this.results.addEventListener('pointermove', event => { if (event.pointerType !== 'touch') this.selectResult(event.target); }, options);
      this.results.addEventListener('pointerleave', () => this.selectResult(document.activeElement), options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.dismiss(); }, options);
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.dismiss(); }, options);
      this.querySelector('form').addEventListener('submit', () => this.dismiss(), options);
      window.addEventListener('resize', () => this.fitResults(), options);
      window.addEventListener('scroll', () => this.fitResults(), {...options, capture: true, passive: true});
      window.visualViewport?.addEventListener('resize', () => this.fitResults(), options);
      window.visualViewport?.addEventListener('scroll', () => this.fitResults(), options);
      this.selectionResize?.disconnect();
      this.selectionResize = new ResizeObserver(() => this.positionSelection(true));
      this.selectionResize.observe(this.results);
      this.selectionSettings?.disconnect();
      this.selectionSettings = new MutationObserver(() => this.positionSelection(true));
      this.selectionSettings.observe(this, {attributes: true, attributeFilter: ['data-qs-width', 'data-qs-selection']});
      this.clear.hidden = !this.input.value;
      // A native side menu stays mounted while closed. Invalidate requests there too.
      this.host = this.closest('.sm-menu');
      if (this.host) {
        this.observer = new MutationObserver(() => { if (this.host.inert || this.host.getAttribute('aria-hidden') === 'true') this.dismiss(); });
        this.observer.observe(this.host, {attributes:true, attributeFilter:['inert','aria-hidden']});
      }
    }
    disconnectedCallback() { this.listeners?.abort(); this.observer?.disconnect(); this.selectionResize?.disconnect(); this.selectionSettings?.disconnect(); this.dismiss(); }
    selectResult(target) {
      const row = target instanceof Element ? target.closest('.qs-product, .qs-collection') : null;
      const next = row && this.results.contains(row) && !this.results.hidden ? row : null;
      if (next === this.selectedResult) return;
      if (this.selectionBody) this.selectionResize?.unobserve(this.selectionBody);
      this.selectedResult?.removeAttribute('data-qs-selected');
      this.selectedResult = next;
      this.selectionBody = next?.querySelector('.qs-item') || next;
      if (!next) { if (this.cursor) this.cursor.hidden = true; return; }
      next.setAttribute('data-qs-selected', '');
      if (!this.cursor?.isConnected) {
        this.cursor = document.createElement('span');
        this.cursor.className = 'qs-cursor'; this.cursor.hidden = true;
        this.cursor.setAttribute('aria-hidden', 'true'); this.results.append(this.cursor);
      }
      this.positionSelection(this.cursor.hidden);
      this.selectionResize?.observe(this.selectionBody);
    }
    positionSelection(instant = false) {
      if (!this.selectedResult?.isConnected || this.results.hidden || !this.cursor) return;
      const target = this.dataset.qsWidth === 'capped' ? this.selectionBody : this.selectedResult;
      const bounds = target.getBoundingClientRect();
      const panel = this.results.getBoundingClientRect();
      const geometry = {
        '--qs-cursor-x': `${bounds.left - panel.left - this.results.clientLeft + this.results.scrollLeft}px`,
        '--qs-cursor-y': `${bounds.top - panel.top - this.results.clientTop + this.results.scrollTop}px`,
        '--qs-cursor-width': `${bounds.width}px`,
        '--qs-cursor-height': `${bounds.height}px`
      };
      // The first ResizeObserver delivery must not cancel an in-flight move.
      if (!this.cursor.hidden && Object.entries(geometry).every(([key, value]) => this.cursor.style.getPropertyValue(key) === value)) return;
      this.cursor.classList.toggle('is-instant', instant);
      for (const [key, value] of Object.entries(geometry)) this.cursor.style.setProperty(key, value);
      this.cursor.hidden = false;
    }
    dismiss() {
      this.selectResult(null);
      clearTimeout(this.timer); this.timer = null; this.request?.abort(); this.request = null;
      clearTimeout(this.loadingTimer);
      this.revision = (this.revision || 0) + 1;
      if (this.results) { this.results.hidden = true; this.results.removeAttribute('aria-busy'); this.results.classList.remove('is-entering', 'is-waiting'); this.results.style.minHeight = ''; }
      if (this.status) this.status.textContent = '';
      this.dataset.state = 'idle';
      this.classList.remove('has-results');
    }
    schedule() {
      const previousHeight = this.results.hidden ? 0 : this.results.getBoundingClientRect().height;
      this.dismiss(); this.clear.hidden = !this.input.value;
      const term = this.input.value.trim();
      if (!term) return;
      this.lastQuery = term;
      if (previousHeight) this.showWaiting(term, previousHeight);
      this.timer = setTimeout(() => this.load(term), 120);
    }
    fullSearchLink(term, fallback = false) {
      const link = document.createElement('a'); link.className = 'qs-all'; link.dataset.qsLink = '';
      const destination = new URL(this.querySelector('form').action); destination.search = new URLSearchParams({q:term,'options[prefix]':'last'});
      link.href = destination;
      const label = document.createElement('span'); label.textContent = `${fallback ? 'Search the store' : 'All results'} for “${term}”`;
      const arrow = document.createElement('span'); arrow.className = 'qs-go'; arrow.setAttribute('aria-hidden', 'true'); arrow.textContent = '→';
      link.append(label, arrow); return link;
    }
    showPanel(animate) {
      this.results.hidden = false; this.classList.add('has-results');
      this.results.classList.toggle('is-entering', Boolean(animate));
      this.fitResults();
    }
    showWaiting(term, previousHeight = 0) {
      const wasHidden = this.results.hidden;
      const waiting = document.createElement('div'); waiting.className = 'qs-waiting';
      const message = document.createElement('p'); message.textContent = 'Searching…'; waiting.append(message);
      this.results.replaceChildren(waiting, this.fullSearchLink(term));
      this.results.classList.add('is-waiting'); this.results.setAttribute('aria-busy', 'true');
      if (previousHeight) this.results.style.minHeight = `${previousHeight}px`;
      this.showPanel(wasHidden && !previousHeight);
    }
    fitResults() {
      if (this.results.hidden) return;
      const viewport = window.visualViewport;
      let top = viewport?.offsetTop || 0;
      let bottom = top + (viewport?.height || window.innerHeight);
      if (this.host) { const bounds = this.host.getBoundingClientRect(); bottom = Math.min(bottom, bounds.bottom); top = Math.max(top, bounds.top); }
      const field = this.querySelector('.qs-field').getBoundingClientRect();
      const below = bottom - field.bottom - 18, above = field.top - top - 18;
      const lift = below < 180 && above > below;
      this.dataset.placement = lift ? 'above' : 'below';
      const height = Math.max(44, Math.min(580, lift ? above : below));
      this.results.style.maxHeight = `${height}px`;
      if (this.results.style.minHeight) this.results.style.minHeight = `${Math.min(height, parseFloat(this.results.style.minHeight))}px`;
      this.positionSelection(true);
    }
    async load(term) {
      this.timer = null;
      const revision = this.revision;
      const url = new URL(this.dataset.endpoint, location.origin);
      url.search = new URLSearchParams({q:term, 'section_id':'quick-search', 'resources[type]':'product,collection', 'resources[limit]':'4', 'resources[limit_scope]':'each', 'resources[options][unavailable_products]':'last'});
      const key = url.href;
      const current = () => this.isConnected && revision === this.revision && this.input.value.trim() === term && this.contains(document.activeElement) && !this.closest('[inert], [hidden]');
      this.status.textContent = 'Searching…';
      this.dataset.state = 'loading';
      this.results.setAttribute('aria-busy','true');
      try {
        let html; const stored = cache.get(key);
        if (stored && Date.now() - stored.at < 60000) html = stored.html;
        else {
          this.loadingTimer = setTimeout(() => { if (current() && this.results.hidden) this.showWaiting(term); }, 180);
          const request = new AbortController(); this.request = request;
          const response = await fetch(url, {signal: request.signal});
          if (!response.ok) throw Error(response.status);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = doc.querySelector('[data-qs-response]');
          if (!content) throw Error('Missing suggestions');
          html = content.innerHTML;
          if (!current()) return;
          if (cache.size >= 20) cache.delete(cache.keys().next().value);
          cache.set(key, {html, at:Date.now()});
        }
        if (!current()) return;
        clearTimeout(this.loadingTimer);
        const wasHidden = this.results.hidden;
        // A shopper can reach the full-search link before a slow reply arrives.
        // Keep focus in the search instead of discarding its focused element.
        if (this.results.contains(document.activeElement)) this.input.focus();
        this.results.innerHTML = html;
        this.request = null;
        this.dataset.state = 'ready'; this.results.classList.remove('is-waiting'); this.results.style.minHeight = '';
        this.results.removeAttribute('aria-busy'); this.showPanel(wasHidden);
        const count = this.results.querySelectorAll('.qs-product').length;
        const collections = this.results.querySelectorAll('.qs-collection').length;
        this.status.textContent = count || collections ? `${count} product ${count === 1 ? 'match' : 'matches'}${collections ? ` and ${collections} ${collections === 1 ? 'collection' : 'collections'}` : ''}. Use the down arrow to explore.` : 'No quick product matches. Search the full store or try another word.';
      } catch (error) {
        if (error.name === 'AbortError' || !current()) return;
        clearTimeout(this.loadingTimer); this.request = null;
        const wasHidden = this.results.hidden;
        if (this.results.contains(document.activeElement)) this.input.focus();
        this.results.replaceChildren(this.fullSearchLink(term, true));
        this.dataset.state = 'error'; this.results.classList.remove('is-waiting'); this.results.style.minHeight = '';
        this.results.removeAttribute('aria-busy'); this.showPanel(wasHidden);
        this.status.textContent = 'Quick results are unavailable. Full search is still available.';
      }
    }
    onKey(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape' && (!this.results.hidden || this.request || this.timer)) {
        event.preventDefault(); event.stopPropagation(); this.dismiss(); this.suppressFocus = true; this.input.focus(); this.suppressFocus = false; return;
      }
      if (this.results.hidden || !['ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
      if (event.target === this.input && ['Home','End'].includes(event.key)) return;
      const links = Array.from(this.results.querySelectorAll('[data-qs-link]'));
      if (!links.length) return;
      event.preventDefault(); event.stopPropagation();
      const index = links.indexOf(document.activeElement);
      let next = event.key === 'Home' ? 0 : event.key === 'End' ? links.length - 1 : index + (event.key === 'ArrowDown' ? 1 : -1);
      if (event.target === this.input) next = event.key === 'ArrowDown' ? 0 : links.length - 1;
      if (next < 0 || next >= links.length) this.input.focus(); else links[next].focus();
    }
  }
  customElements.define('quick-search', QuickSearch);
})();
